// src/screens/FAQScreen.js
// ============================================================================
// FAQ — answers the "why / what / how" questions a first-time visitor asks
// before signing up. Public (pre-login) screen.
//
// Lexicon: this is skill-competition copy. We answer the "do I risk money"
// concern with positive non-cash/skill framing and never use casino/betting
// language (see brand lexicon rules). Copy must match the Terms of Service
// (v1.4): entry is free, results and placements are never paid, and the only
// money is creator compensation from Super Crits and Rumbles, which CritCash
// pays from its own revenue. "Plan" = the platform product.
//
// Terms v1.5 (evergreen 3.5a): once channel memberships have ever launched
// (useMembershipsLive().everLaunched), the compensation-source answers also
// name channel memberships (faqsFor). Dark, faqsFor(false) is FAQS unchanged.
//
// Terms v1.6 (evergreen 3.5b): faqsFor also takes teamEverLaunched; the
// compensation-source lists name team memberships once they have ever
// launched (four states, see faqsFor).
// ============================================================================

import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Header from '../components/Header';
import Footer from '../components/Footer';
import useMembershipsLive from '../hooks/useMembershipsLive';

const COLORS = {
  BG: '#0b0b0b',
  CARD: '#101010',
  BORDER: '#1c1c1c',
  GOLD: '#FCD000',
  CYAN: '#3aafff',
  TEXT: '#EDEDED',
  DIM: '#9aa0b4',
};

// Q&A content. `a` is an array of paragraphs.
// The dark (never launched) answers; faqsFor adds memberships after launch.
const FAQS = [
  {
    q: 'What is CritCash?',
    a: [
      "CritCash is a streaming platform for gamers, with the tools built in to grow and engage your audience. Go live on any game you play, save your streams as videos, stream with your squad, and run your own events and leagues.",
      "Free to start: going live costs nothing, and viewers watch free with no account.",
    ],
  },
  {
    q: 'Does it cost anything to enter?',
    a: [
      "No. Every event on CritCash is free to enter: no entry cost, no purchase required. Sign up, connect your stream, and compete. Your placements go on your record and the leaderboards.",
    ],
  },
  {
    q: 'Why is entry free?',
    a: [
      "Free entry keeps competition open to everyone, everywhere. CritCash keeps score on results — participation, placements, consistency — so every match adds to a record that persists.",
      "Results and placements are never paid. Verified creators and players can earn compensation only from the Super Crits and Rumbles they receive, paid by CritCash from its own revenue. See the creator program question below.",
    ],
  },
  {
    q: 'Do I have to pay cash to play?',
    a: [
      "No. You never pay cash to participate. Entry to every event is free.",
      "CritCash is a streaming platform for gamers, and events, leagues, and tournaments are one of its tools, with creators recognized for showing up, competing, and bringing the crowd. Skill decides your matches, never chance, but the platform is your stream, your community, and the record you build.",
    ],
  },
  {
    q: 'How do Super Crits and Rumbles work?',
    a: [
      "Super Crits are highlighted messages that light up the chat on a live stream. Rumbles light up the crowd meter on a live stream, a squad, or a live event. Both are optional purchases from CritCash, never required to watch or to play, and the amounts you can pick are shown when you buy. If your account isn't verified, Super Crits, Rumbles and the first payment of a new membership together are limited to $500 in any 24 hours, counted for your account and for each card; verifying your account removes the limit.",
      "A Super Crit or a stream Rumble supports the verified streamer it's sent to. Sent to a squad, either one is shared equally among the verified squad members streaming when it's sent. An event Rumble is shared equally among the verified players who played, whatever their placement, once results are final, and is refunded if the event is cancelled, isn't completed, or no one in it can receive a share.",
      "See the creator program question below for how CritCash compensates the creators and players who receive them.",
    ],
  },
  {
    q: 'How do plans work?',
    a: [
      "Every plan, free included, keeps your stream recordings as Videos for its retention period. Some paid plans keep them longer. On a free account, or after a paid plan ends, videos you upload yourself may be removed after a period we set; we'll email you first, with time to download them.",
      "Pay monthly, or go yearly to save — yearly is a few months free. You can switch between them anytime, and going yearly kicks in right away. Paid plans are optional — entry to every event is free.",
    ],
  },
  {
    q: 'What is the creator program?',
    a: [
      "Verified creators and players can earn compensation from CritCash, paid from its own revenue. When you receive a Super Crit or a Rumble, CritCash credits you a share of the purchase, currently up to 70% after payment-processing costs (lower on small purchases). It stays pending until it's payable, no sooner than 48 hours after the purchase.",
      "Compensation comes only from Super Crits and Rumbles. Results and placements are never paid.",
      "You don't need a paid plan. Identity and age (18+) verification is the only requirement to receive compensation, and nothing is credited before you verify. Payable earnings are paid automatically to the payout account you connect.",
    ],
  },
  {
    q: 'How are results confirmed?',
    a: [
      "Download CritCast for Windows (it sets up OBS for you) or stream your event with one stream key through OBS or Streamlabs. Console players connect a video capture card to their PC or laptop. On supported games, results are verified automatically, straight from your stream.",
      "On other games, players report the result, and our team reviews any dispute using stream recordings where available. Some formats, such as battle royale, are scored by our team.",
    ],
  },
  {
    q: 'Can I follow my chat while I stream?',
    a: [
      "Yes. Every channel owner gets a chat-only page that never loads the video, so keeping an eye on chat costs you no stream bandwidth. Open it from Streams → Your channel → \"Pop out your chat\", or go straight to critcash.com/chat/YourUsername.",
      "It also works as a panel inside your broadcaster: in OBS Studio add it under Docks → Custom Browser Docks; in Streamlabs Desktop open the Layout Editor, drag in the Website element, and paste the same URL. Sign in once inside the panel and you can chat and pin as yourself. The chat-only view is for the channel owner — anyone else who opens it is taken to the stream page instead.",
    ],
  },
  {
    q: 'How old do I have to be?',
    a: [
      "You must be 18 or older to create an account and use CritCash. It's an adults-only platform.",
    ],
  },
  {
    q: 'Does my record ever reset?',
    a: [
      "Your record is yours to keep. Wins, placements, hosting, and certified sellouts stay on your profile.",
      "Your record carries forward.",
    ],
  },
  {
    q: 'What games can I play?',
    a: [
      "Gran Turismo 7, Madden, Call of Duty, and Marvel Rivals are live right now, with Dota 2 and CS2 up next — and more landing across racing, sports, FPS, and MOBA.",
      "One account, one stream key, whatever you play.",
    ],
  },
  {
    q: 'What are Leagues?',
    a: [
      "Leagues are season-long competitions you commit to. Entry is free: play a schedule of rounds against the field, and the final standings go on your record.",
      "Three kinds: solo round-robin (everyone plays everyone), full-grid racing leagues (the whole roster races together each round, scored on a points system), and team leagues where a captain builds a squad. Browse or post one in the League section.",
    ],
  },
  {
    q: 'What do I get for filling my events?',
    a: [
      "Fill every seat of a full-size event you created with real, verified players — and finish it — and it certifies as a Verified Sellout about 48 hours after it wraps. Certified sellouts go on your permanent record: the Full Crown badge on your profile, with a running count that never resets.",
      "A certified sellout also unlocks Run It Back on that event's page: one tap reruns the same event for the same crowd, a week out at the same time, and everyone who showed up ready gets the call. Leagues run it back as a next season with the roster invited back.",
    ],
  },
  {
    q: 'What does it cost?',
    a: [
      "Nothing. Entry to every event is free.",
      "Paid plans are optional — for players who want extra perks. Some paid plans keep Videos longer.",
    ],
  },
  {
    q: 'Where is CritCash available?',
    a: [
      "CritCash is open worldwide: wherever you are, sign up and go live.",
      "You must be 18 or older, and prices are shown in USD.",
    ],
  },
];

// Paragraph swaps once memberships have ever launched (design 13.8), keyed by
// the exact dark text so the dark answers stay byte-identical. The purchase
// limit sentence in "How do Super Crits and Rumbles work?" (Terms v1.6, tiers
// design 5.8) stays: it is true either way.
const FAQ_MEMBERSHIP_SWAPS = {
  "Results and placements are never paid. Verified creators and players can earn compensation only from the Super Crits and Rumbles they receive, paid by CritCash from its own revenue. See the creator program question below.":
    "Results and placements are never paid. Verified creators and players can earn compensation only from the Super Crits and Rumbles they receive and from memberships to their channels, paid by CritCash from its own revenue. See the creator program question below.",
  "Verified creators and players can earn compensation from CritCash, paid from its own revenue. When you receive a Super Crit or a Rumble, CritCash credits you a share of the purchase, currently up to 70% after payment-processing costs (lower on small purchases). It stays pending until it's payable, no sooner than 48 hours after the purchase.":
    "Verified creators and players can earn compensation from CritCash, paid from its own revenue. When you receive a Super Crit or a Rumble, CritCash credits you a share of the purchase, currently up to 70% after payment-processing costs (lower on small purchases). A verified creator is also credited a share of each membership payment to their channel, including renewals, on the same terms. It stays pending until it's payable, no sooner than 48 hours after the purchase.",
  "Compensation comes only from Super Crits and Rumbles. Results and placements are never paid.":
    "Compensation comes only from Super Crits, Rumbles and channel memberships. Results and placements are never paid.",
};

// Team memberships (evergreen 3.5b, design 25.8): the answers that name
// channel memberships as the whole set gain the team kind once team
// memberships have ever launched (useMembershipsLive().teamEverLaunched).
// Channel and team launched: "channel and team memberships"; team only: "team
// memberships", with no channel availability claimed (the other channel
// sentences keep their dark text). Keyed by the exact dark text, like the
// channel swaps.
const FAQ_CHANNEL_AND_TEAM_SWAPS = {
  "Results and placements are never paid. Verified creators and players can earn compensation only from the Super Crits and Rumbles they receive, paid by CritCash from its own revenue. See the creator program question below.":
    "Results and placements are never paid. Verified creators and players can earn compensation only from the Super Crits and Rumbles they receive and from memberships to their channels and teams, paid by CritCash from its own revenue. See the creator program question below.",
  "Compensation comes only from Super Crits and Rumbles. Results and placements are never paid.":
    "Compensation comes only from Super Crits, Rumbles and channel and team memberships. Results and placements are never paid.",
};
const FAQ_TEAM_ONLY_SWAPS = {
  "Results and placements are never paid. Verified creators and players can earn compensation only from the Super Crits and Rumbles they receive, paid by CritCash from its own revenue. See the creator program question below.":
    "Results and placements are never paid. Verified creators and players can earn compensation only from the Super Crits and Rumbles they receive and from memberships to their teams, paid by CritCash from its own revenue. See the creator program question below.",
  "Compensation comes only from Super Crits and Rumbles. Results and placements are never paid.":
    "Compensation comes only from Super Crits, Rumbles and team memberships. Results and placements are never paid.",
};

// Four states on (channel everLaunched, teamEverLaunched): (false, false) is
// FAQS unchanged; (true, false) is the 3.5a launched answers unchanged.
export function faqsFor(everLaunched, teamEverLaunched = false) {
  if (!everLaunched && !teamEverLaunched) return FAQS;
  const swaps = !teamEverLaunched
    ? FAQ_MEMBERSHIP_SWAPS
    : everLaunched
      ? { ...FAQ_MEMBERSHIP_SWAPS, ...FAQ_CHANNEL_AND_TEAM_SWAPS }
      : FAQ_TEAM_ONLY_SWAPS;
  return FAQS.map((f) => ({ ...f, a: f.a.map((p) => swaps[p] || p) }));
}

function FaqItem({ q, a, open, onToggle }) {
  return (
    <View style={styles.item}>
      <TouchableOpacity style={styles.qRow} onPress={onToggle} activeOpacity={0.7}>
        <Text style={styles.qText}>{q}</Text>
        <Ionicons
          name={open ? 'chevron-up' : 'chevron-down'}
          size={18}
          color={COLORS.GOLD}
        />
      </TouchableOpacity>
      {open && (
        <View style={styles.aWrap}>
          {a.map((p, i) => (
            <Text key={i} style={styles.aText}>{p}</Text>
          ))}
        </View>
      )}
    </View>
  );
}

export default function FAQScreen({ navigation }) {
  const [openIdx, setOpenIdx] = useState(0);
  const { everLaunched, teamEverLaunched } = useMembershipsLive();
  const faqs = faqsFor(everLaunched, teamEverLaunched);

  return (
    <View style={styles.root}>
      <Header navigation={navigation} />
      <ScrollView contentContainerStyle={styles.scroll} showsVerticalScrollIndicator={false}>
        <View style={styles.hero}>
          <Text style={styles.eyebrow}>QUESTIONS, ANSWERED</Text>
          <Text style={styles.title}>How CritCash Works</Text>
          <Text style={styles.subtitle}>
            Free to start, free to watch, and the tools are built in. Here's everything
            people ask before they jump in.
          </Text>
        </View>

        <View style={styles.list}>
          {faqs.map((f, i) => (
            <FaqItem
              key={i}
              q={f.q}
              a={f.a}
              open={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
            />
          ))}
        </View>

        <View style={styles.ctaWrap}>
          <TouchableOpacity
            style={styles.primaryCta}
            onPress={() => navigation.navigate('SignUp')}
          >
            <Text style={styles.primaryCtaText}>Start Free</Text>
            <Ionicons name="arrow-forward" size={16} color="#000" style={{ marginLeft: 8 }} />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.secondaryCta}
            onPress={() => navigation.navigate('HowToPlay')}
          >
            <Text style={styles.secondaryCtaText}>How to Play & Stream</Text>
          </TouchableOpacity>
        </View>

        <Text style={styles.disclaimer}>
          Free to start. Events are decided by skill, never chance. Open worldwide to anyone 18 and older.
        </Text>

        <Footer navigation={navigation} />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: COLORS.BG },
  scroll: { flexGrow: 1, paddingHorizontal: 20, paddingBottom: 20 },

  hero: {
    alignItems: 'center',
    paddingTop: 48,
    paddingBottom: 28,
    maxWidth: 720,
    alignSelf: 'center',
    width: '100%',
  },
  eyebrow: {
    color: COLORS.CYAN,
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 2,
    marginBottom: 12,
  },
  title: {
    color: COLORS.TEXT,
    fontSize: 32,
    fontWeight: '900',
    letterSpacing: -0.5,
    textAlign: 'center',
    marginBottom: 12,
  },
  subtitle: {
    color: COLORS.DIM,
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
    maxWidth: 540,
  },

  list: {
    maxWidth: 720,
    alignSelf: 'center',
    width: '100%',
    gap: 10,
  },
  item: {
    backgroundColor: COLORS.CARD,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    borderRadius: 12,
    paddingHorizontal: 16,
    ...(Platform.OS === 'web' ? { boxShadow: '0 4px 18px rgba(0,0,0,0.35)' } : {}),
  },
  qRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    gap: 12,
  },
  qText: {
    color: COLORS.TEXT,
    fontSize: 16,
    fontWeight: '700',
    flex: 1,
  },
  aWrap: {
    paddingBottom: 16,
    gap: 10,
  },
  aText: {
    color: COLORS.DIM,
    fontSize: 14,
    lineHeight: 21,
  },

  ctaWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'center',
    marginTop: 36,
  },
  primaryCta: {
    backgroundColor: COLORS.GOLD,
    paddingVertical: 14,
    paddingHorizontal: 28,
    borderRadius: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { boxShadow: '0 0 24px rgba(252, 208, 0, 0.35)' } : {}),
  },
  primaryCtaText: { color: '#000', fontWeight: '800', fontSize: 15 },
  secondaryCta: {
    backgroundColor: 'transparent',
    paddingVertical: 14,
    paddingHorizontal: 28,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    alignItems: 'center',
    justifyContent: 'center',
  },
  secondaryCtaText: { color: COLORS.TEXT, fontWeight: '700', fontSize: 15 },

  disclaimer: {
    color: '#555',
    fontSize: 11,
    lineHeight: 16,
    textAlign: 'center',
    marginTop: 28,
    maxWidth: 540,
    alignSelf: 'center',
  },
});
